import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { AxiosRequestConfig } from 'axios';
import axiosInstance from 'config/axios';
import { PaginationResponse } from 'interfaces/pagination-response';
import { Therapy } from 'interfaces/therapy';

/**
 * @description Therapy response type
 * @type {TherapyResponse}
 */
export type TherapyResponse = PaginationResponse<Therapy>;

/**
 * @description Therapy service
 * @type {TherapyService}
 */
const TherapyService = {
  /**
   * @description Create therapy
   * @param {Partial<Therapy>} data - Therapy data
   * @returns {Promise<Therapy>} Therapy
   */
  create: (data: Partial<Therapy>) => {
    return axiosInstance.post<Therapy>('/v1/therapies', data);
  },

  /**
   * @description Update therapy
   * @param {string | number} id - Therapy id
   * @param {Partial<Therapy>} data - Therapy data
   * @returns {Promise<Therapy>} Therapy
   */
  update: (id: string | number, data: Partial<Therapy>) => {
    return axiosInstance.patch<Therapy>(`/v1/therapies/${id}`, data);
  },

  /**
   * @description Find therapy
   * @param {string | number} id - Therapy id
   * @param {AxiosRequestConfig} config - Axios request config
   * @returns {Promise<Therapy>} Therapy
   */
  find: (id: string | number, config?: AxiosRequestConfig) => {
    return axiosInstance.get<Therapy>(`/v1/therapies/${id}`, config);
  },

  /**
   * @description Delete therapy
   * @param {string | number} id - Therapy id
   * @returns {Promise<Therapy>} Therapy
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<Therapy>(`/v1/therapies/${id}`);
  },

  /**
   * @description Get therapies
   * @param {Object} queryParams - Query parameters
   * @returns {Promise<TherapyResponse>} Therapy response
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
  }) => {
    return axiosInstance.get<TherapyResponse>('/v1/therapies', {
      params: queryParams,
    });
  },
};

export default TherapyService;
